/* Account writes for the console (2026-10-28), moved out of the classic
   dashboard's Accounts tab. Each is the classic handler (BusTracking.tsx),
   with its rules-allowed fields:

   - invites: handleBulkInvite / handleResendInvite → sendFleetInvite, and the
     fleetInvites listener with its stale-invite sweep;
   - suspendDriver: the Accounts modal's suspend / unsuspend;
   - setTransportHold: setChildrenTransportSuspend (the parent keeps their login);
   - moveChildren: moveChildrenToBus;
   - removeAccount: handleRemoveAccount, conductor path included. */
import { useEffect, useState } from 'react';
import {
  collection, deleteDoc, doc, FieldValue, getDoc, onSnapshot, query, serverTimestamp, Timestamp, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { sendFleetInvite } from '../../components/FleetJoinPage';
import { disableTripConductor, isTripsApiConfigured } from '../../services/tripsApi';
import type { FleetModel, Person } from './model';

export type InviteRole = 'parent' | 'bus_driver';
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface PendingInvite { id: string; email: string; role: InviteRole; createdAt: Date | null }

/** Invites still waiting for someone to join. Classic's sweep comes along:
 *  superseded invites, and pending ones older than a day, are deleted. */
export function usePendingInvites(orgCode: string): PendingInvite[] {
  const [list, setList] = useState<PendingInvite[]>([]);
  useEffect(() => {
    if (!orgCode) { setList([]); return undefined; }
    const DAY = 24 * 60 * 60 * 1000;
    return onSnapshot(query(collection(db, 'fleetInvites'), where('orgCode', '==', orgCode)), (snap) => {
      const now = Date.now();
      const out: PendingInvite[] = [];
      snap.docs.forEach((d) => {
        const v = d.data() as Record<string, unknown>;
        if (v.used === true) return;
        const created = (v.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? now;
        if (v.superseded === true || now - created > DAY) {
          deleteDoc(doc(db, 'fleetInvites', d.id)).catch(() => undefined);
          return;
        }
        out.push({ id: d.id, email: String(v.email || ''), role: v.role === 'bus_driver' ? 'bus_driver' : 'parent', createdAt: new Date(created) });
      });
      out.sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0));
      setList(out);
    }, () => setList([]));
  }, [orgCode]);
  return list;
}

const isPilot = (f: FleetModel) => f.org.raw.isPilot === true || f.org.raw.subscriptionPlan === 'pilot';

export interface InviteOutcome { sent: number; failed: string[]; skipped: number; rateLimited: boolean; lastError: string }

/** Classic handleBulkInvite: one at a time, skip what is already pending,
 *  stop on a rate limit, and hand back every address that still needs sending. */
export async function sendInvites(
  f: FleetModel, uid: string, role: InviteRole, emails: string[], pending: PendingInvite[],
): Promise<InviteOutcome> {
  const already = new Set(pending.filter((p) => p.role === role).map((p) => p.email));
  const toSend = emails.filter((e) => !already.has(e));
  const out: InviteOutcome = { sent: 0, failed: [], skipped: emails.length - toSend.length, rateLimited: false, lastError: '' };
  for (let i = 0; i < toSend.length; i += 1) {
    const email = toSend[i];
    const r = await sendFleetInvite(email, f.org.code, f.org.name || f.org.code, role, uid, { isTester: false, isPilot: isPilot(f) });
    if (r.ok) { out.sent += 1; continue; }
    out.failed.push(email);
    out.lastError = r.error || '';
    if (/too many|too-many-requests/i.test(out.lastError)) {
      out.rateLimited = true;
      out.failed.push(...toSend.slice(i + 1));
      break;
    }
  }
  return out;
}

export async function resendInvite(f: FleetModel, uid: string, inv: PendingInvite): Promise<void> {
  const r = await sendFleetInvite(inv.email, f.org.code, f.org.name || f.org.code, inv.role, uid, { isTester: false, isPilot: isPilot(f) });
  if (!r.ok) throw new Error(r.error || 'resend failed');
}

export const isSuspended = (p: Person) => p.raw.suspended === true;

/** The Accounts modal's suspend / unsuspend: the app listens to isLoggedOut. */
export async function suspendDriver(p: Person, suspend: boolean): Promise<void> {
  await updateDoc(doc(db, 'users', p.id), suspend ? {
    suspended: true,
    suspendedAt: serverTimestamp(),
    suspendReason: 'Your account has been suspended by your fleet admin. Contact them to restore access.',
    isLoggedOut: true,
    logoutReason: 'Your account has been suspended by your fleet admin.',
    logoutTimestamp: serverTimestamp(),
  } : {
    suspended: false,
    suspendedAt: null,
    suspendReason: null,
    isLoggedOut: false,
    logoutReason: null,
    logoutTimestamp: null,
  });
}

type RosterRow = Record<string, unknown>;
const rosterOfRaw = (raw: Record<string, unknown>): RosterRow[] => (Array.isArray(raw.children) ? (raw.children as RosterRow[]) : []);

/** A parent's children: their roster rows (by parentId, or by a childId the
 *  parent's own record lists) with the bus, then any child of theirs that is
 *  on no bus list — shown, with nothing to move or hold. */
export function childrenOf(f: FleetModel, p: Person): { childId: string; name: string; busId: string; busName: string; held: boolean; onRoster: boolean }[] {
  const mine = new Set(p.children.map((c) => c.id).filter(Boolean));
  const out: { childId: string; name: string; busId: string; busName: string; held: boolean; onRoster: boolean }[] = [];
  for (const b of f.buses) {
    for (const c of rosterOfRaw(b.raw)) {
      const cid = String(c.childId || '');
      if (String(c.parentId || '') !== p.id && !(cid && mine.has(cid))) continue;
      out.push({ childId: cid, name: String(c.childName || c.name || ''), busId: b.id, busName: b.name, held: c.transportSuspended === true, onRoster: true });
    }
  }
  for (const c of p.children) {
    if (out.some((o) => (c.id && o.childId === c.id) || o.name.toLowerCase() === c.name.toLowerCase())) continue;
    out.push({ childId: c.id, name: c.name, busId: c.busId || '', busName: '', held: false, onRoster: false });
  }
  return out;
}

/** Classic setChildrenTransportSuspend: flag the roster rows, keep the login. */
export async function setTransportHold(f: FleetModel, childIds: string[], hold: boolean): Promise<void> {
  const ids = new Set(childIds);
  const busIds = f.buses.filter((b) => rosterOfRaw(b.raw).some((c) => ids.has(String(c.childId || '')))).map((b) => b.id);
  if (busIds.length === 0) throw new Error('No children on a bus roster to update');
  const batch = writeBatch(db);
  let ops = 0;
  for (const bid of busIds) {
    const snap = await getDoc(doc(db, 'buses', bid));
    if (!snap.exists()) continue;
    const next = rosterOfRaw(snap.data() as Record<string, unknown>).map((c) => {
      if (!ids.has(String(c.childId || ''))) return c;
      const copy = { ...c };
      if (hold) copy.transportSuspended = true; else delete copy.transportSuspended;
      return copy;
    });
    batch.update(doc(db, 'buses', bid), { children: next, updatedAt: serverTimestamp() });
    ops += 1;
  }
  if (ops === 0) throw new Error('Could not load the bus roster');
  await batch.commit();
}

/** Classic moveChildrenToBus: off the old roster (status reset), onto the new
 *  one, and the parent's record repointed so their app shows the bus — both
 *  `busChildren` (what classic repointed) and each child row's `busId`, which
 *  is what the parent app reads (home_screen.dart). Classic left the rows, so
 *  the app kept showing the old bus after an office move. */
export async function moveChildren(f: FleetModel, parent: Person, childIds: string[], targetBusId: string): Promise<number> {
  const parentId = parent.id;
  const target = f.buses.find((b) => b.id === targetBusId);
  if (!target) throw new Error('That bus is not in your fleet.');
  const targetRef = doc(db, 'buses', target.id);
  const tSnap = await getDoc(targetRef);
  const targetRows = tSnap.exists() ? [...rosterOfRaw(tSnap.data() as Record<string, unknown>)] : [];
  const sources: Record<string, RosterRow[]> = {};
  const moved: string[] = [];
  for (const row of childrenOf(f, parent)) {
    if (!row.onRoster || !childIds.includes(row.childId) || row.busId === target.id) continue;
    if (!sources[row.busId]) {
      const s = await getDoc(doc(db, 'buses', row.busId));
      sources[row.busId] = s.exists() ? [...rosterOfRaw(s.data() as Record<string, unknown>)] : [];
    }
    const arr = sources[row.busId];
    const i = arr.findIndex((c) => String(c.childId || '') === row.childId);
    if (i < 0) continue;
    const entry: RosterRow = { ...arr[i], status: 'not_picked', lastUpdated: Timestamp.now() };
    arr.splice(i, 1);
    if (!targetRows.some((c) => String(c.childId || '') === row.childId)) targetRows.push(entry);
    moved.push(String(entry.childName || row.name));
  }
  if (moved.length === 0) return 0;
  const batch = writeBatch(db);
  Object.entries(sources).forEach(([bid, rows]) => batch.update(doc(db, 'buses', bid), { children: rows, updatedAt: serverTimestamp() }));
  batch.update(targetRef, { children: targetRows, updatedAt: serverTimestamp() });
  const pRef = doc(db, 'users', parentId);
  const pSnap = await getDoc(pRef);
  const pData = (pSnap.exists() ? pSnap.data() : {}) as Record<string, unknown>;
  const bc = { ...((pData.busChildren as Record<string, string>) || {}) };
  moved.forEach((nm) => { if (nm) bc[nm] = target.id; });
  const movedIds = new Set(childIds);
  const movedNames = new Set(moved.map((n) => n.toLowerCase()));
  const rows = (Array.isArray(pData.children) ? (pData.children as RosterRow[]) : []).map((c) => {
    const cid = String(c.childId || c.id || '');
    const nm = String(c.childName || c.name || '').toLowerCase();
    return (cid && movedIds.has(cid)) || (nm && movedNames.has(nm)) ? { ...c, busId: target.id } : c;
  });
  batch.update(pRef, { busChildren: bc, children: rows, updatedAt: serverTimestamp() });
  await batch.commit();
  return moved.length;
}

/** Classic handleRemoveAccount, field for field. Throws with a message a
 *  person can act on. */
export async function removeAccount(f: FleetModel, p: Person, myUid: string): Promise<void> {
  if (p.id === myUid) throw new Error('You cannot remove your own account.');
  const userRef = doc(db, 'users', p.id);
  const snap = await getDoc(userRef);
  if (!snap.exists()) throw new Error('That account no longer exists.');
  const data = snap.data() as Record<string, unknown>;
  const isTempConductor = data.temporaryTripConductor === true || p.id.startsWith('tripc_') || !!(data.username && !p.email);

  if (isTempConductor && f.org.code && isTripsApiConfigured()) {
    try {
      const r = await disableTripConductor({ orgCode: f.org.code, uid: p.id, username: typeof data.username === 'string' ? data.username : undefined });
      if (r.disabled !== false) return;
    } catch (e) {
      if ((e as { status?: number })?.status === 403) throw new Error((e as Error).message || 'You are not allowed to remove this conductor.');
    }
  }

  const batch = writeBatch(db);
  const updates: Record<string, unknown> = {
    isLoggedOut: true,
    logoutReason: 'removed_by_fleet_admin',
    logoutTimestamp: serverTimestamp(),
    updatedAt: serverTimestamp(),
    schoolCode: null,
    organizationCode: null,
    institution: null,
    orgCode: null,
    activeSchoolTripId: null,
    suspended: true,
    suspendedAt: serverTimestamp(),
    suspendReason: 'Removed by fleet admin',
  };
  if (isTempConductor) {
    updates.temporaryTripConductor = false;
    updates.tripConductorAccessUntil = Timestamp.fromMillis(Date.now() - 1000);
  }
  const busId = (p.busId || data.busId) as string | undefined;
  if (busId) {
    updates.busId = null;
    updates.isBusOwner = null;
    updates.busNickname = null;
    try {
      const bSnap = await getDoc(doc(db, 'buses', busId));
      if (bSnap.exists()) {
        const ids = ((bSnap.data().driverIds as string[]) || []).filter((id) => id !== p.id);
        batch.update(doc(db, 'buses', busId), { driverIds: ids, updatedAt: serverTimestamp() });
      }
    } catch { /* bus unassign skipped, as in classic */ }
  }
  for (const b of f.buses) {
    if (!rosterOfRaw(b.raw).some((c) => c.parentId === p.id)) continue;
    try {
      const r = await getDoc(doc(db, 'buses', b.id));
      if (!r.exists()) continue;
      const rows = rosterOfRaw(r.data() as Record<string, unknown>);
      const kept = rows.filter((c) => c.parentId !== p.id);
      if (kept.length !== rows.length) batch.update(doc(db, 'buses', b.id), { children: kept, updatedAt: serverTimestamp() });
    } catch { /* roster strip skipped, as in classic */ }
  }
  const busChildren = (data.busChildren as Record<string, string>) || {};
  const ours = new Set(f.buses.map((b) => b.id));
  if (Object.values(busChildren).some((bid) => ours.has(bid))) {
    updates.children = ((data.children as Record<string, unknown>[]) || []).map((c) => {
      const bid = (c as { busId?: string }).busId;
      return bid && ours.has(bid) ? { ...c, busId: null } : c;
    });
    const nb = { ...busChildren };
    Object.keys(nb).forEach((k) => { if (ours.has(nb[k])) delete nb[k]; });
    updates.busChildren = nb;
  }
  batch.update(userRef, updates as { [k: string]: FieldValue | Partial<unknown> | undefined });
  await batch.commit();
}

/** Roster rows whose parent is gone or never linked — classic showed these
 *  and nothing could fix them; the console can take the row off the bus. */
export function unlinkedChildren(f: FleetModel): { busId: string; busName: string; childId: string; name: string }[] {
  const parentIds = new Set(f.parents.map((p) => p.id));
  const out: { busId: string; busName: string; childId: string; name: string }[] = [];
  for (const b of f.buses) {
    for (const c of rosterOfRaw(b.raw)) {
      const pid = String(c.parentId || '');
      if (pid && parentIds.has(pid)) continue;
      out.push({ busId: b.id, busName: b.name, childId: String(c.childId || ''), name: String(c.childName || c.name || 'A child') });
    }
  }
  return out;
}

export async function removeRosterRow(busId: string, childId: string, name: string): Promise<void> {
  const ref = doc(db, 'buses', busId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That bus is gone.');
  const rows = rosterOfRaw(snap.data() as Record<string, unknown>);
  const kept = rows.filter((c) => (childId ? String(c.childId || '') !== childId : String(c.childName || c.name || '') !== name));
  await updateDoc(ref, { children: kept, updatedAt: serverTimestamp() });
}
